'use client';

import type { ReactNode, RefObject } from 'react';
import {
  clientItems,
  projectItems,
  workItems,
  type Item,
} from '@/data/experience';
import { flipTimeline as flip, usePulseStore } from '@/features/pulse';
import styles from './Salutation.module.css';
import { Typography } from '@/ui';

const byAnchor = new Map<string, Item>(
  [...workItems, ...clientItems, ...projectItems].map((i) => [i.anchor, i])
);

const LINKS = [
  { label: 'CV', href: '/ondrej-barta-cv.pdf' },
  { label: 'GitHub', href: 'https://github.com/bartaxyz' },
  { label: 'Email', href: 'mailto:email@ondrejbarta.com' },
];

const Mention = ({
  anchor,
  children,
}: {
  anchor: string;
  children: ReactNode;
}) => {
  const setHoverIds = usePulseStore((state) => state.setHoverIds);
  const ids = byAnchor.get(anchor)?.pulseIds ?? null;

  return (
    <Typography.Link
      className={styles.mention}
      href={`#${anchor}`}
      onMouseEnter={() => setHoverIds(ids)}
      onMouseLeave={() => setHoverIds(null)}
      onFocus={() => setHoverIds(ids)}
      onBlur={() => setHoverIds(null)}
    >
      {children}
    </Typography.Link>
  );
};

const WorkBio = () => (
  <>
    <p>
      Engineering leader, over a decade building software, 5+ years leading
      teams.
    </p>
    <p>
      I run a team at <Mention anchor="glassnode">Glassnode</Mention>, behind
      Studio, data viz, the public APIs and more.
    </p>
    <p>
      I built <Mention anchor="bitwala-nuri">Bitwala</Mention>&apos;s apps from
      scratch and grew with it past 500,000 customers.
    </p>
    <p>
      I consult through <Mention anchor="kilta">Kilta</Mention>, and ship my own
      things: <Mention anchor="tabpilot">TabPilot</Mention>, featured by Apple,
      and <Mention anchor="gitpt">GitPT</Mention>, an on-device commit-message
      CLI.
    </p>
    <p>Full-stack, design-minded, hands on.</p>
  </>
);

const LifeBio = () => (
  <>
    <p>Reading, writing, sculpting, and always learning.</p>
    <p>
      I make things in my own time too, because I like making them. Useful or
      beautiful, ideally both.
    </p>
    <p>
      I&apos;m still involved in Kilta, and a member of TechSquat, a non-profit
      co-work and co-live community.
    </p>
    <p>
      Below, the signals I actually track: where the week goes, and a heart rate
      off my wrist.
    </p>
  </>
);

export const Salutation = ({
  shell = 'work',
  pulseSlotRef,
  nav,
}: {
  shell?: 'work' | 'life';
  pulseSlotRef?: RefObject<HTMLDivElement | null>;
  nav?: ReactNode;
}) => (
  <div className={styles.root}>
    <div className={styles.header}>
      <h1 className={styles.name}>Ondřej Bárta</h1>
      {nav}
    </div>

    <div className={styles.body}>
      <div className={styles.stack}>
        <div className={styles.layer} data-shown={shell === 'work'}>
          <WorkBio />
        </div>
        <div className={styles.layer} data-shown={shell === 'life'}>
          <LifeBio />
        </div>
      </div>

      <nav className={styles.links} aria-label="Elsewhere">
        {LINKS.map(({ label, href }) => (
          <Typography.Link href={href} key={href}>
            {label}
          </Typography.Link>
        ))}
      </nav>
    </div>

    <div
      ref={pulseSlotRef}
      className={`${styles.pulseSlot} ${flip.slot}`}
      aria-hidden={true}
    />
  </div>
);
